import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import store, { IAppState } from "../../state/Store";
import Datastore from "../../communication/Datastore";
import UserRole from "../../types/core/UserRole";
import Utilities from "../../foundation/Utility";
import { REHYDRATE } from "redux-persist";
import { getAllUsers } from "./User";
import User from "../../types/core/User";

// Define the type for our state store entry
type UserRoleState = {
    userRoles: UserRole[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: UserRoleState = {
    userRoles: null,
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const userRolesSlice = createSlice({
    name: "userRoles",
    initialState: initialState,
    reducers: {
        updateRole: (state, action: PayloadAction<UserRole>) => {
            let role = state.userRoles.find(
                role => role.id === action.payload.id,
            );

            if (role) Utilities.merge(role, action.payload);
        },
        recalcRoles: (state, action: PayloadAction<User[]>) => {
            for (const role of state.userRoles) {
                const users = action.payload.filter(user =>
                    user.roles.includes(role.name),
                );

                role.userCount = users.length;
                role.availableCount = role.licenceCount - role.userCount;
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    userRoles: rawData?.payload?.userRoles?.userRoles ?? [],
                    status:
                        !rawData?.payload?.userRoles?.userRoles ||
                        !rawData?.payload?.userRoles?.userRoles?.length
                            ? "idle"
                            : rawData?.payload?.userRoles?.status ===
                              "loading"
                            ? "idle"
                            : rawData?.payload?.userRoles?.status,
                    error: null,
                };
            })
            .addCase(fetchUserRoles.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchUserRoles.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                for (const role of action.payload.roles) {
                    const users = action.payload.users.filter(user =>
                        user.roles.includes(role.name),
                    );

                    role.userCount = users.length;
                    role.availableCount = role.licenceCount - role.userCount;
                }
                state.userRoles = action.payload.roles;
            })
            .addCase(fetchUserRoles.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateUserRolesDetails.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateUserRolesDetails.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                action.payload.forEach(role =>
                    state.userRoles.splice(
                        state.userRoles.indexOf(
                            state.userRoles.find(item => item.id === role.id),
                        ),
                        1,
                        role,
                    ),
                );
            })
            .addCase(updateUserRolesDetails.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

export const { recalcRoles } = userRolesSlice.actions;

// This is a thunk, they are used to load the state
const fetchUserRoles = createAsyncThunk(
    "school/fetchUserRoles",
    async (_, thunkAPI) => {
        const [roles, users] = await Promise.all([
            Datastore.getType<UserRole[]>("api/UserRoles"),
            thunkAPI.dispatch(getAllUsers()),
        ]);

        return { roles: roles, users: users.payload as User[] };
    },
);
export const updateUserRolesDetails = createAsyncThunk(
    "school/updateUserRolesDetails",
    async (userRoles: UserRole[], thunkAPI) => {
        const response = await Promise.all(
            userRoles.map(role =>
                Datastore.postType<UserRole>("api/UserRoles", role),
            ),
        );
        return response;
    },
);

export const initialiseUserRolesDetails = createAsyncThunk<UserRole[]>(
    "school/initialiseUserRoles",
    async (_, thunkAPI) => {
        const { userRoles } = store.getState().db;

        if (userRoles.status === "idle")
            await thunkAPI.dispatch(fetchUserRoles());

        return store.getState().db.userRoles.userRoles;
    },
);

// We have to export the reducer as this is added to the state store
export default userRolesSlice.reducer;
